import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, ChevronDown, Loader as Loader2, Mail, CheckSquare, Square } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { fetchWorkspaceMembers, WorkspaceMemberWithProfile } from '@/lib/workspaceMembers';
import { generateHMACSignature } from '@/components/Sites/utils';
import type { Site } from '../types';
import type { UptimeCheck, TimeRange } from './types';
import { TIME_RANGE_OPTIONS } from './types';
import { UptimeStatsCards } from './UptimeStatsCards';
import { ResponseTimeChart } from './ResponseTimeChart';
import { UptimeStatusBar } from './UptimeStatusBar';
import { LastCheckDetails } from './LastCheckDetails';
import { useUptimeRealtime } from './useUptimeRealtime';

type SubTab = 'activity' | 'settings';

interface UptimeMonitorTabProps {
  site: Site;
  onUpdateSite: (updates: Partial<Site>) => void;
}

async function saveCheck(check: Omit<UptimeCheck, 'id'>): Promise<void> {
  await supabase.from('site_uptime_checks').insert(check);
}

export const UptimeMonitorTab: React.FC<UptimeMonitorTabProps> = ({ site, onUpdateSite }) => {
  const [subTab, setSubTab] = useState<SubTab>('activity');
  const [timeRange, setTimeRange] = useState<TimeRange>('3h');
  const [rangeOpen, setRangeOpen] = useState(false);
  const [checks, setChecks] = useState<UptimeCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingInterval, setSavingInterval] = useState(false);

  const [members, setMembers] = useState<WorkspaceMemberWithProfile[]>([]);
  const [notifyEmails, setNotifyEmails] = useState<string[]>(site.uptime_notify_emails || []);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);

  useEffect(() => {
    if (site.workspace_id) {
      fetchWorkspaceMembers(site.workspace_id).then(setMembers);
    }
  }, [site.workspace_id]);

  const toggleNotifyEmail = (email: string) => {
    if (!email) return;
    const newEmails = notifyEmails.includes(email) 
      ? notifyEmails.filter(e => e !== email) 
      : [...notifyEmails, email];
    
    setNotifyEmails(newEmails);
  };

  const saveNotifications = async () => {
    setIsSavingNotifications(true);
    await supabase.from('workspace_sites').update({ uptime_notify_emails: notifyEmails }).eq('id', site.id);
    onUpdateSite({ uptime_notify_emails: notifyEmails });
    setIsSavingNotifications(false);
  };

  const selectedRange = TIME_RANGE_OPTIONS.find(r => r.value === timeRange)!;

  const fetchChecks = useCallback(async () => {
    if (!site.id) return;
    const since = new Date(Date.now() - selectedRange.hours * 3600000).toISOString();
    const { data } = await supabase
      .from('site_uptime_checks')
      .select('*')
      .eq('site_id', site.id)
      .gte('created_at', since)
      .order('created_at', { ascending: false });
    if (data) setChecks(data as UptimeCheck[]);
    setLoading(false);
  }, [site.id, selectedRange.hours]);

  const runCheck = useCallback(async () => {
    if (!site || site.status !== 'connected' || checking) return;
    setChecking(true);
    const startTime = performance.now();
    try {
      const formattedUrl = site.url.replace(/\/+$/, '');
      const endpoint = `${formattedUrl}/wp-json/agencyplus/v1/ping`;
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const canonical = `${timestamp}\nGET\n/agencyplus/v1/ping\n`;
      const signature = await generateHMACSignature(site.secretKey, canonical);

      let isUp = false;
      let statusCode: number | null = null;
      let totalMs = 0;
      let dnsMs: number | null = null;
      let tcpMs: number | null = null;
      let sslMs: number | null = null;
      let serverMs: number | null = null;
      let downloadMs: number | null = null;
      let serverIp: string | null = null;

      try {
        const cacheBuster = `cb=${Date.now()}`;
        const finalUrl = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${cacheBuster}`;
        const response = await fetch(finalUrl, {
          method: 'GET',
          headers: {
            'X-AgencyPlus-Public-Key': site.publicKey,
            'X-AgencyPlus-Signature': signature,
            'X-AgencyPlus-Timestamp': timestamp,
          },
        });

        totalMs = performance.now() - startTime;
        statusCode = response.status;
        isUp = response.ok;
        
        let pluginServerMs: number | null = null;
        if (isUp) {
          try {
            const data = await response.json();
            pluginServerMs = data.server_ms || null;
            serverIp = data.server_ip || null;
          } catch { }
        }

        await new Promise(r => setTimeout(r, 400));
        const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const entry = [...entries].reverse().find(e => e.name.includes(cacheBuster));
        
        if (entry && entry.domainLookupEnd > 0) {
          dnsMs = entry.domainLookupEnd - entry.domainLookupStart;
          tcpMs = entry.connectEnd - entry.connectStart;
          sslMs = entry.secureConnectionStart > 0 ? entry.connectEnd - entry.secureConnectionStart : 0;
          serverMs = pluginServerMs || (entry.responseStart - entry.requestStart);
          downloadMs = entry.responseEnd - entry.responseStart;
        } else {
          serverMs = pluginServerMs || (totalMs * 0.75);
          downloadMs = totalMs - (serverMs || 0);
        }
      } catch (err) {
        totalMs = performance.now() - startTime;
        serverMs = totalMs * 0.75;
        downloadMs = totalMs * 0.25;
      }

      const checkData: Omit<UptimeCheck, 'id'> = {
        site_id: site.id,
        status: isUp ? 'up' : 'down',
        status_code: statusCode,
        total_time_ms: totalMs,
        dns_ms: dnsMs,
        tcp_ms: tcpMs,
        ssl_ms: sslMs,
        server_ms: serverMs,
        download_ms: downloadMs,
        server_ip: serverIp,
        redirects: 0,
        created_at: new Date().toISOString(),
      };

      await saveCheck(checkData);
      
      // Update both cases for consistency and persist to DB
      const newStatus = isUp ? 'up' : 'down';
      const lastCheck = new Date().toISOString();
      
      await supabase.from('workspace_sites').update({
        uptime_status: newStatus,
        uptime_last_check: lastCheck,
        ...(isUp ? { uptime_last_up_at: lastCheck } : { uptime_last_down_at: lastCheck })
      }).eq('id', site.id);

      onUpdateSite({ 
        uptime_status: newStatus, 
        uptimeStatus: newStatus,
        uptime_last_check: lastCheck 
      });
      await fetchChecks();
    } catch (err) {
      console.error('Uptime check fatal error:', err);
    } finally {
      setChecking(false);
    }
  }, [site, checking, fetchChecks, onUpdateSite]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  const rangeHoursRef = useRef(selectedRange.hours);
  rangeHoursRef.current = selectedRange.hours;

  const handleNewCheck = useCallback((check: UptimeCheck) => {
    const since = Date.now() - rangeHoursRef.current * 3600000;
    if (new Date(check.created_at).getTime() >= since) {
      setChecks(prev => [check, ...prev]);
    }
  }, []);

  const handleSiteUpdate = useCallback((fields: Record<string, unknown>) => {
    const updates: Partial<Site> = {};
    if (fields.uptime_status !== undefined) {
      updates.uptime_status = fields.uptime_status as Site['uptime_status'];
      updates.uptimeStatus = fields.uptime_status as Site['uptime_status'];
    }
    if (fields.uptime_last_check !== undefined) updates.uptime_last_check = fields.uptime_last_check as string;
    if (fields.uptime_last_up_at !== undefined) updates.uptime_last_up_at = fields.uptime_last_up_at as string;
    if (fields.uptime_last_down_at !== undefined) updates.uptime_last_down_at = fields.uptime_last_down_at as string;
    if (fields.uptime_enabled !== undefined) updates.uptime_enabled = fields.uptime_enabled as boolean;
    if (Object.keys(updates).length > 0) onUpdateSite(updates);
  }, [onUpdateSite]);

  useUptimeRealtime(site.id, {
    onNewCheck: handleNewCheck,
    onSiteUpdate: handleSiteUpdate,
  });

  const toggleUptime = async () => {
    setSavingToggle(true);
    const newVal = !site.uptime_enabled;
    await supabase
      .from('workspace_sites')
      .update({ uptime_enabled: newVal, ...(newVal ? {} : { uptime_status: 'unknown' }) })
      .eq('id', site.id);
    onUpdateSite({ uptime_enabled: newVal, ...(!newVal ? { uptime_status: 'unknown' } : {}) });
    setSavingToggle(false);
  };

  const updateInterval = async (interval: number) => {
    setSavingInterval(true);
    await supabase.from('workspace_sites').update({ uptime_interval: interval }).eq('id', site.id);
    onUpdateSite({ uptime_interval: interval });
    setSavingInterval(false);
  };

  const latestCheck = checks[0] ?? null;
  const isEnabled = site.uptime_enabled !== false;

  const subTabs = [
    { id: 'activity' as SubTab, label: 'Activity' },
    { id: 'settings' as SubTab, label: 'Settings' },
  ];

  return (
    <div className="space-y-6 w-full">
      <div className="flex items-center gap-1 border-b border-border">
        {subTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${subTab === t.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t.label}
            {subTab === t.id && (
              <motion.div layoutId="uptime-subtab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-workspace-accent" />
            )}
          </button>
        ))}
      </div>

      {subTab === 'activity' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`relative w-9 h-5 rounded-full transition-colors ${isEnabled ? 'bg-emerald-500' : 'bg-muted'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${isEnabled ? 'left-[18px]' : 'left-0.5'}`} />
              </div>
              <span className={`text-sm font-semibold ${isEnabled ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                {isEnabled ? 'ON' : 'OFF'}
              </span>
              <span className="text-sm text-muted-foreground">Uptime monitor {isEnabled ? 'activated' : 'disabled'}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={runCheck}
                disabled={checking || site.status !== 'connected'}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} />
                {checking ? 'Checking...' : 'Refresh'}
              </button>

              <div className="relative">
                <button
                  onClick={() => setRangeOpen(o => !o)}
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {selectedRange.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${rangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {rangeOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setRangeOpen(false)} />
                    <div className="absolute right-0 top-full mt-1.5 w-40 bg-popover border border-border rounded-xl shadow-xl z-20 overflow-hidden py-1">
                      {TIME_RANGE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setTimeRange(opt.value); setRangeOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-xs transition-colors hover:bg-muted/50 ${timeRange === opt.value ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <UptimeStatsCards checks={checks} site={site} />

              <div className="rounded-xl border border-border bg-card p-5 space-y-5">
                <ResponseTimeChart checks={checks} timeRange={timeRange} />
                <UptimeStatusBar checks={checks} timeRange={timeRange} rangeHours={selectedRange.hours} checkIntervalMin={site.uptime_interval ?? 5} />
              </div>

              <LastCheckDetails check={latestCheck} />
            </>
          )}
        </div>
      )}

      {subTab === 'settings' && (
        <div className="space-y-4 max-w-md">
          <div className="rounded-xl border border-border bg-card p-5 space-y-5">
            <h3 className="text-sm font-semibold text-foreground">Monitor Settings</h3>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Uptime Monitoring</p>
                <p className="text-xs text-muted-foreground mt-0.5">Periodically check if your site is online</p>
              </div>
              <button
                onClick={toggleUptime}
                disabled={savingToggle}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${isEnabled ? 'bg-emerald-500' : 'bg-muted'} disabled:opacity-60`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${isEnabled ? 'left-[26px]' : 'left-1'}`} />
              </button>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium text-foreground mb-3">Check Frequency</p>
              <div className="flex gap-2">
                {[5, 10].map(interval => (
                  <button
                    key={interval}
                    onClick={() => updateInterval(interval)}
                    disabled={savingInterval}
                    className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-60 ${
                      (site.uptime_interval ?? 5) === interval
                        ? 'border-workspace-accent bg-workspace-accent/10 text-workspace-accent'
                        : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                    }`}
                  >
                    Every {interval} min
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Notifications</p>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Select workspace members to receive an email when this site goes offline or comes back online.
              </p>
              
              <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                {members.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Loading members...</p>
                ) : (
                  members.map(member => {
                    if (!member.email) return null;
                    const isSelected = notifyEmails.includes(member.email);
                    return (
                      <button
                        key={member.id}
                        onClick={() => toggleNotifyEmail(member.email)}
                        className="w-full flex items-center justify-between p-2.5 rounded-lg border border-border bg-muted/30 hover:bg-muted/60 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {member.avatar ? (
                            <img src={member.avatar} alt={member.name} className="w-6 h-6 rounded-full" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-workspace-accent/20 flex items-center justify-center text-[10px] font-bold text-workspace-accent">
                              {member.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="text-left">
                            <p className="text-xs font-semibold text-foreground">{member.name}</p>
                            <p className="text-[10px] text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-workspace-accent" />
                        ) : (
                          <Square className="w-4 h-4 text-muted-foreground" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
              
              <button
                onClick={saveNotifications}
                disabled={isSavingNotifications}
                className="w-full mt-4 py-2 bg-workspace-accent text-white text-xs font-semibold rounded-lg hover:bg-workspace-accent/90 transition-colors disabled:opacity-50"
              >
                {isSavingNotifications ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="text-sm font-semibold text-foreground mb-1">About Uptime Monitoring</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Uptime checks are performed automatically by a background cron job at the configured frequency. The dashboard displays all historical check results from the server. Use the Refresh button to run an on-demand check outside of the scheduled intervals.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
